import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AuthContextType {
  isAuthenticated: boolean;
  token: string | null;
  email: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const TOKEN_KEY = 'dc-copilot-token';
const AUTH_STATUS_KEY = 'dc-copilot-auth';
const EMAIL_KEY = 'dc-copilot-email';

/**
 * Read auth parameters from URL and store them in localStorage.
 * This allows authentication data to be passed from a different origin/port.
 * Returns the auth data if found in URL, otherwise returns data from localStorage.
 */
function getInitialAuthFromURL(): { token: string | null; email: string | null; isAuthenticated: boolean } {
  // Check URL parameters first
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get(TOKEN_KEY);
  const urlAuth = urlParams.get(AUTH_STATUS_KEY);
  const urlEmail = urlParams.get(EMAIL_KEY);

  // If URL contains auth parameters, store them in localStorage
  if (urlToken && urlEmail) {
    localStorage.setItem(TOKEN_KEY, urlToken);
    localStorage.setItem(AUTH_STATUS_KEY, urlAuth || 'authenticated');
    localStorage.setItem(EMAIL_KEY, urlEmail);

    // Clean up URL by removing auth parameters (optional but recommended for security)
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete(TOKEN_KEY);
    newUrl.searchParams.delete(AUTH_STATUS_KEY);
    newUrl.searchParams.delete(EMAIL_KEY);
    window.history.replaceState({}, '', newUrl.pathname + newUrl.search);

    return {
      token: urlToken,
      email: urlEmail,
      isAuthenticated: true
    };
  }

  // Fall back to localStorage
  const storedToken = localStorage.getItem(TOKEN_KEY);
  const storedEmail = localStorage.getItem(EMAIL_KEY);

  return {
    token: storedToken,
    email: storedEmail,
    isAuthenticated: !!storedToken
  };
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize from URL params or localStorage
  const initialAuth = getInitialAuthFromURL();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(initialAuth.isAuthenticated);

  const [token, setToken] = useState<string | null>(initialAuth.token);

  const [email, setEmail] = useState<string | null>(initialAuth.email);

  useEffect(() => {
    // Update localStorage when auth status changes
    if (isAuthenticated && token && email) {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(AUTH_STATUS_KEY, 'authenticated');
      localStorage.setItem(EMAIL_KEY, email);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(AUTH_STATUS_KEY);
      localStorage.removeItem(EMAIL_KEY);
    }
  }, [isAuthenticated, token, email]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Dynamic import to avoid circular dependency
      const { login: loginApi } = await import('../services/api');
      
      const response = await loginApi({ email, password });
      
      if (response.statusCode === 200 && response.data?.token) {
        setToken(response.data.token);
        setEmail(email); // Save the email
        setIsAuthenticated(true);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setToken(null);
    setEmail(null);
    setIsAuthenticated(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(AUTH_STATUS_KEY);
    localStorage.removeItem(EMAIL_KEY);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, token, email, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};




